/**
 * Step 2 - Mapeamento de colunas do CSV aos campos do negócio.
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-16 v1.1.0 - Banner de aviso quando o CSV contém
 *            cabeçalhos sensíveis (moeda, empresa). Esses campos são
 *            sempre ignorados server-side em process_deal_import_batch.
 *   RELEASE: 2026-05-16 v1.0.0 - Versão inicial.
 */

import { useMemo } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DEAL_FIELDS, SENSITIVE_HEADERS } from './types';

interface Step2MappingProps {
  rowCount: number;
  headers: string[];
  mapping: Record<string, string>;
  onMappingChange: (next: Record<string, string>) => void;
}

export function Step2Mapping({ rowCount, headers, mapping, onMappingChange }: Step2MappingProps) {
  // Detecta cabeçalhos sensíveis presentes no CSV para sinalizar ao usuário
  // que serão ignorados pelo backend, evitando importação acidental.
  const ignoredHeaders = useMemo(
    () =>
      headers.filter((h) =>
        SENSITIVE_HEADERS.includes(h.trim().toLowerCase())
      ),
    [headers]
  );

  // Helper que atualiza/remove uma entrada do mapping de forma imutável.
  function setField(fieldKey: string, value: string) {
    const next = { ...mapping };
    if (value === '__none__') delete next[fieldKey];
    else next[fieldKey] = value;
    onMappingChange(next);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {rowCount.toLocaleString('pt-BR')} linhas detectadas. Mapeie cada coluna do CSV ao campo do negócio.
      </p>

      {/* Aviso: colunas sensíveis (moeda/empresa) são ignoradas por segurança */}
      {ignoredHeaders.length > 0 && (
        <div className="flex gap-3 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-amber-900 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-100">
          <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <p className="font-medium">
              Colunas ignoradas por segurança: {ignoredHeaders.map((h) => `"${h}"`).join(', ')}
            </p>
            <p className="text-xs opacity-90">
              A moeda dos negócios é sempre BRL e a empresa não pode ser
              importada via CSV (evita risco de associação incorreta de dados).
              Você pode manter essas colunas no arquivo — elas não serão usadas.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {DEAL_FIELDS.map((f) => (
          <div key={f.key} className="grid grid-cols-2 gap-2 items-center">
            <Label className="text-sm">
              {f.label}
              {f.required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Select
              value={mapping[f.key] || '__none__'}
              onValueChange={(v) => setField(f.key, v)}
            >
              <SelectTrigger><SelectValue placeholder="— não mapear —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— não mapear —</SelectItem>
                {headers.map((h) => (
                  <SelectItem key={h} value={h}>{h}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}
